/* import __COLOCATED_TEMPLATE__ from './stage.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import FilterConstants from '../../filter-constants';
import ErrorTracker from '../../utils/error-tracker';
import type OpAmpsService from 'filter-wizard/services/opamps';
import type IntlService from 'ember-intl/services/intl';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type ComponentsAdvisor from '../../models/advisors/component-selection';
import type IndividualStageInput from '../../models/inputs/component-selection/individual-stage';
import type SallenKeyLowPassIdealOpAmp from 'filter-wizard/models/implementation/sallen-key/low-pass/ideal-op-amp';
import {
  DeviceColumn,
  DeviceData,
  PackedFilterValue,
  SortColumnRef,
} from '@ebiz/ember-aditools-device-table/components/aditools-device-table';

interface ComponentStageSettingsArgs {
  model: IndividualStageInput;
  advisor: ComponentsAdvisor;
  index: number;
  workingVsPlus: number;
  workingVsMinus: number;
  stageLetter: string;
  errorTracker: ErrorTracker;
  update: () => void;
  isDisabledByErrors: boolean;
  systemDialogFunc?: (status: string, message: string, msgparams?: Record<string, unknown>) => void;
  showNotification?: (title: string, message: string, msgparams?: Record<string, unknown>) => void;
}

type Limits = {
  allowedLimits: SallenKeyLowPassIdealOpAmp;
  recommendedLimits: SallenKeyLowPassIdealOpAmp;
  powerSpan: number;
  stageLetter: string | undefined;
};

export default class ComponentStageSettings extends Component<ComponentStageSettingsArgs> {
  @service declare opamps: OpAmpsService;
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: SiteService;

  @tracked opAmpDialogEnabled: boolean;
  @tracked filterType: string;

  constructor(owner: unknown, args: ComponentStageSettingsArgs) {
    super(owner, args);
    this.opAmpDialogEnabled = false;
    this.filterType = this.args.advisor.model.inputs.filterType;
    this.opamps.getOpAmps();
  }

  get implementationList(): { text: string; value: string }[] {
    return this.getImplementationList();
  }

  get implementationSelection(): { text: string; value: string } | undefined {
    const selected = this.args.model.implementation;
    const selection = this.implementationList.find((item) => {
      return item.value === selected;
    });
    return selection;
  }

  get limits(): Limits {
    const currentIndex = this.args.index;
    const stageLetter = this.args.stageLetter;
    const stageLimits = this.args.advisor.getStageLimits(
      this.args.model,
      this.args.workingVsPlus,
      this.args.workingVsMinus,
      currentIndex,
    );
    return {
      allowedLimits: stageLimits.allowedLimits,
      recommendedLimits: stageLimits.recommendedLimits,
      powerSpan: stageLimits.powerSpan,
      stageLetter: stageLetter,
    };
  }

  get displayGainSlider(): boolean {
    const isGainEnabled = this.args.model.gainRatio.enabled;
    const implementation = this.args.model.implementation;
    return isGainEnabled && implementation !== FilterConstants.implementationType.multipleFeedback;
  }

  get displayQResistors(): boolean {
    //Displays Q Resistors label on gain slider when filterType is bandpass
    const display = this.filterType === FilterConstants.filterType.bandPass ? true : false;
    return display;
  }

  get getOpAmpDisplay(): string {
    return this.args.model.opAmp !== 'ideal'
      ? this.args.model.opAmp
      : this.intl.t('settings.components.choose-op-amp').toString();
  }

  get isOpAmpError(): boolean {
    return this.args.errorTracker.isInErrorList('stageOpAmp' + this.args.index);
  }

  get selectionFilterOptions(): {
    columns?: DeviceColumn<DeviceData>[];
    filters?: PackedFilterValue[];
    sortColumns?: SortColumnRef[];
  } {
    return this.opamps.selectionFilterOptions;
  }

  ////////////////////////////////////////////////////////////////////////////////////
  //                                                                                //
  // There will be instances where the componentSizing range equals to one value:   //
  //                                                                                //
  //      model.componentSizing.MINIMUM === model.componentSizing.MAXIMUM           //
  //                                                                                //
  // In such a case, the slider position gets set on the rightmost area of the      //
  // slider bar. The computed properties below works to handle this, adjusting the  //
  // stage display in a manner that the slider knob is set to the middle of slider  //
  // bar. The computations affect the stage displays only, with no foreseen effects //
  // to the model.                                                                  //
  //                                                                                //
  //      IF (componentSizing.minimum === componentSizng.maximum) {                 //
  //          0 <===== x =====> componentSizing.maximum*2                           //
  //      } ELSE {                                                                  //
  //          componentSizing.minimum <===== x =====> componentSizing.maximum       //
  //      }                                                                         //
  //                                                                                //
  ////////////////////////////////////////////////////////////////////////////////////

  get minimumSizingValue(): number {
    const maximumValue = this.args.model.componentSizing.maximum;
    const minimumValue = this.args.model.componentSizing.minimum;
    // This adds a slider range LOWER LIMIT of 0 when both the ACTUAL range's lower and upper limits are equal.
    // Otherwize, it utilizes the ACTUAL minimum value as lower limit.
    //     min == x == max    ---->   0  < x
    return minimumValue === maximumValue ? 0 : minimumValue;
  }

  get maximumSizingValue(): number {
    const maximumValue = this.args.model.componentSizing.maximum;
    const minimumValue = this.args.model.componentSizing.minimum;
    // This adds a slider range UPPER LIMIT of maximumValue*2 when both the ACTUAL range's lower and upper
    // limits are equal. Otherwize, it utilizes the ACTUAL maximum value as upper limit.
    //     min == x == max    ---->   x < max*2
    return minimumValue === maximumValue ? maximumValue * 2 : maximumValue;
  }

  get sizingEnabled(): boolean {
    const maximumValue = this.args.model.componentSizing.maximum;
    const minimumValue = this.args.model.componentSizing.minimum;
    // This just sets the disabled attribute on the slider after it had set the stage-level slider limits
    // implemented the minimumSizingValue and maximumSizingValue.
    return minimumValue === maximumValue ? true : false;
  }

  getImplementationList(): { text: string; value: string }[] {
    const currentIndex = this.args.index;
    const stageDefault = this.args.advisor.getStageDefault(currentIndex);
    const implementationList = [];

    if (stageDefault !== null) {
      const implementationData = stageDefault.implementationSelection;
      for (let i = 0; i < implementationData.selections.length; i++) {
        switch (implementationData.selections[i]) {
          case FilterConstants.implementationType.sallenKey:
            implementationList.push({
              text: 'settings.components.implementations.sallen-key',
              value: FilterConstants.implementationType.sallenKey,
            });
            break;
          case FilterConstants.implementationType.multipleFeedback:
            implementationList.push({
              text: 'settings.components.implementations.multiple-feedback',
              value: FilterConstants.implementationType.multipleFeedback,
            });
            break;
          case FilterConstants.implementationType.inductor:
            implementationList.push({
              text: 'settings.components.implementations.inductor',
              value: FilterConstants.implementationType.inductor,
            });
            break;
          case FilterConstants.implementationType.firstOrder:
            implementationList.push({
              text: 'settings.components.implementations.buffered-rc',
              value: FilterConstants.implementationType.firstOrder,
            });
            break;
          case FilterConstants.implementationType.deliyannisFriend:
            implementationList.push({
              text: 'settings.components.implementations.multiple-feedback',
              value: FilterConstants.implementationType.deliyannisFriend,
            });
            break;
          default:
            break;
        }
      }
    }
    return implementationList;
  }

  updateStageModel(): void {
    // Send Action to Parent Component (if provided)
    if (this.args.update) {
      this.args.update();
    }
  }

  updateSliderValues(implementation: string): void {
    const currentIndex = this.args.index;
    const stageDefaults = this.args.advisor.getImplementationDefaults(currentIndex, implementation);
    this.args.model.componentSizing = stageDefaults.componentSizing;
    this.args.model.gainRatio = stageDefaults.gainRatio;
  }

  @action setImplementation(implementation: { value: string }): void {
    if (this.args.model.implementation !== implementation.value) {
      this.args.model.implementation = implementation.value;
      this.args.model.implementationSelection.selectedItem = implementation.value;
      this.updateSliderValues(implementation.value);
      this.updateStageModel();
    }
  }

  @action updateComponentSizing(e: { target: { value: string } }): void {
    this.args.model.componentSizing.value = Number(e.target.value);
    this.updateStageModel();
  }

  @action updateGainRatio(e: { target: { value: string } }): void {
    this.args.model.gainRatio.value = Number(e.target.value);
    this.updateStageModel();
  }

  @action async showOpAmpSelectionDialog(newState: boolean): Promise<void> {
    if (newState) {
      if (!this.opamps.pstView || !this.opamps.pstMapping) {
        if (this.args.systemDialogFunc) this.args.systemDialogFunc('ON', 'loading-opamp-data');
        this.opamps.pstView = await this.opamps.pstViewPromise;
        this.opamps.pstMapping = await this.opamps.pstMappingPromise;
        if (this.args.systemDialogFunc) this.args.systemDialogFunc('OFF', '');
        if (!this.opamps.pstView || !this.opamps.pstMapping) {
          if (this.args.showNotification)
            this.args.showNotification('dialogs.system.title', 'dialogs.system.error-pst-data');
          return;
        }
      }
    }
    this.opAmpDialogEnabled = newState;
  }

  @action updatePart(part: string): void {
    this.args.model.opAmp = part;
    this.updateStageModel();
  }

  @action updateSelectionFilterOptions(
    columns: DeviceColumn<DeviceData>[] | undefined,
    filters: PackedFilterValue[] | undefined,
    sortColumns: SortColumnRef[] | undefined,
  ): void {
    this.opamps.selectionFilterOptions.columns = columns;
    this.opamps.selectionFilterOptions.filters = filters;
    this.opamps.selectionFilterOptions.sortColumns = sortColumns;
  }
}
